import { z } from 'zod';
import { IdSchema, MoneySchema, TimestampSchema } from './common.js';

/**
 * Payments (docs/28, Phase B1).
 *
 * A client buys airtime by card on PayFast's hosted checkout, so no card data
 * reaches ArcNet. The API signs the checkout for the client's ISSUER, derived
 * from the reseller relationship, and only PayFast's server notification
 * credits the balance.
 */

export const PAYMENT_MODES = ['sandbox', 'live'] as const;
export const PaymentModeSchema = z.enum(PAYMENT_MODES);
export type PaymentMode = z.infer<typeof PaymentModeSchema>;

/** Airtime is bought in rand, within these limits. */
export const AIRTIME_MIN = 50;
export const AIRTIME_MAX = 10_000;
export const AIRTIME_PRESETS = ['100', '250', '500', '1000'] as const;

export const AirtimeAmountSchema = z.string()
  .regex(/^\d{1,5}(\.\d{1,2})?$/, 'An amount in rand, such as 250 or 250.50')
  .refine((v) => Number(v) >= AIRTIME_MIN && Number(v) <= AIRTIME_MAX,
    `Between R${AIRTIME_MIN} and R${AIRTIME_MAX}`);

export const AirtimePurchaseSchema = z.object({ amount: AirtimeAmountSchema }).strict();

export const PaymentSchema = z.object({
  id: IdSchema,
  purpose: z.enum(['airtime']),
  amount: MoneySchema,
  currency: z.string().length(3),
  status: z.enum(['pending', 'complete', 'failed', 'cancelled']),
  mode: PaymentModeSchema,
  provider: z.enum(['payfast']),
  /** PayFast's own reference, once it has notified. */
  provider_payment_id: z.string().nullable(),
  completed_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type Payment = z.infer<typeof PaymentSchema>;

/**
 * A signed checkout. The browser posts `fields`, in order, to `action`:
 * PayFast's hosted payment page.
 */
export const CheckoutSchema = z.object({
  payment: PaymentSchema,
  action: z.string().url(),
  fields: z.array(z.tuple([z.string(), z.string()])),
});
export type Checkout = z.infer<typeof CheckoutSchema>;

/** Who is paid: the name on receipts, and later on tax invoices. */
export const LegalEntitySchema = z.object({
  id: IdSchema,
  kind: z.enum(['platform', 'reseller']),
  registered_name: z.string(),
  trading_name: z.string().nullable(),
  registration_number: z.string().nullable(),
  vat_number: z.string().nullable(),
  address: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  updated_at: TimestampSchema,
});
export type LegalEntity = z.infer<typeof LegalEntitySchema>;

/** Every field is sent; null clears an optional one. */
export const LegalEntityUpsertSchema = z.object({
  registered_name: z.string().trim().min(2).max(160),
  trading_name: z.string().trim().min(1).max(160).nullable(),
  registration_number: z.string().trim().min(1).max(40).nullable(),
  vat_number: z.string().trim().regex(/^4\d{9}$/, 'Ten digits, starting with 4').nullable(),
  address: z.string().trim().min(1).max(400).nullable(),
  email: z.string().trim().email().max(254).nullable(),
  phone: z.string().trim().min(1).max(40).nullable(),
}).strict();
export type LegalEntityUpsert = z.infer<typeof LegalEntityUpsertSchema>;

/** A merchant as the API returns it: whether the passphrase is set, never what it is. */
export const PayfastSettingsSchema = z.object({
  mode: PaymentModeSchema,
  merchant_id: z.string(),
  passphrase_set: z.boolean(),
  enabled: z.boolean(),
  updated_at: TimestampSchema,
});
export type PayfastSettings = z.infer<typeof PayfastSettingsSchema>;

/**
 * The merchant key and passphrase are write-only: left out, the saved ones are
 * kept. The key is needed the first time.
 */
export const PayfastSettingsUpsertSchema = z.object({
  mode: PaymentModeSchema,
  merchant_id: z.string().trim().regex(/^\d{4,20}$/, 'Digits only, as on your PayFast dashboard'),
  merchant_key: z.string().trim()
    .regex(/^[A-Za-z0-9]{8,40}$/, 'Letters and digits, as on your PayFast dashboard').optional(),
  passphrase: z.string().min(1).max(100)
    .refine((v) => v === v.trim(), 'No spaces at the start or end').optional(),
  enabled: z.boolean(),
}).strict();
export type PayfastSettingsUpsert = z.infer<typeof PayfastSettingsUpsertSchema>;
